import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI } from '../api/api';

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            console.log('Fetching application settings...');
            const response = await settingsAPI.getAll();
            if (response.data.success) {
                console.log('Settings fetched successfully:', response.data.data);
                setSettings(response.data.data);
            } else {
                console.error('Failed to fetch settings:', response.data.message);
            }
        } catch (err) {
            console.error('Error loading settings:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            console.log('Settings loading finished.');
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const updateSetting = async (key, value) => {
        try {
            await settingsAPI.update(key, value);
            setSettings(prev => ({ ...prev, [key]: value }));
            return true;
        } catch (err) {
            console.error('Error updating setting:', err);
            return false;
        }
    };

    const updateSettings = async (updates) => {
        try {
            await settingsAPI.bulkUpdate(updates);
            const newSettings = { ...settings };
            updates.forEach(({ key, value }) => {
                newSettings[key] = value;
            });
            setSettings(newSettings);
            return true;
        } catch (err) {
            console.error('Error updating settings:', err);
            return false;
        }
    };

    // Helper getters
    const gstEnabled = settings.GST_ENABLED === true || settings.GST_ENABLED === 'true';
    const cgstRate = parseFloat(settings.CGST_RATE) || 9;
    const sgstRate = parseFloat(settings.SGST_RATE) || 9;
    const currencySymbol = settings.CURRENCY_SYMBOL || '₹';
    const companyName = settings.COMPANY_NAME || 'Buds Spicy Aliyadans';
    const companyLogo = settings.COMPANY_LOGO || '/buds-logo.png';

    const value = {
        settings,
        loading,
        error,
        fetchSettings,
        updateSetting,
        updateSettings,
        // Computed values
        gstEnabled,
        cgstRate,
        sgstRate,
        currencySymbol,
        companyName,
        companyLogo
    };

    return (
        <SettingsContext.Provider value={value}>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
                    <div style={{ textAlign: 'center' }}>
                        <h2>Initializing Spicy Aliyadans...</h2>
                        <p>Loading application settings...</p>
                    </div>
                </div>
            ) : (
                children
            )}
        </SettingsContext.Provider>
    );
};

export default SettingsContext;
